import React from "react";
import { useLocation, useParams } from "react-router-dom";
import { FeatureIds, isCapabilityAvailable } from "@redbelly-tokeniser/mystake-common-sdk";
import type { IconName } from "../../designSystem/components/core/Icon";
import { AuthContext } from "../../auth/AuthProvider";
import { navbarDataMap } from "../../constants/constants";
import { approvalService, companyService } from "../../util";
import { APPROVAL_COUNT_CHANGED_EVENT } from "../approvals/constants";
import { isApproverReadOnly } from "../commons/useApproverReadOnly";
import { useCompanyContext } from "../companies/useCompanyContext";
import { isDistributionsOffered, isSubscriptionsOffered } from "../companies/pdtiFeatureFlags";
import { PENDING_ACCRUED_UNITS_CHANGED } from "../companies/register/pendingAccruedUnits/pendingAccruedUnitsEvents";
import { useYieldFund } from "../companies/companyInformation/shares/yield/useYieldFund";

/**
 * Navigation destination model (data-model § Navigation Destination).
 *
 * Single source of truth that feeds BOTH presentations of the responsive shell:
 * the desktop icon rail and the mobile slide-in drawer (FR-009, FR-010). Derived
 * from the active company/auth context plus `RoutesConfig`/`constants` metadata so
 * the rail and drawer can never drift apart.
 *
 * This mirrors the destination logic that previously lived inside the legacy
 * `Sidebar` component (role visibility, feature flags, active detection) so that
 * adopting `AppShell` is a presentational change only.
 */
export interface NavSecondaryItem {
  id: string;
  label: string;
  /** Route to navigate to. */
  to: string;
  active: boolean;
}

export interface NavDestination {
  /** Stable key, e.g. `holdings`, `investors`, `registry`, `settings`. */
  id: string;
  icon: IconName;
  label: string;
  /** Route to navigate to. */
  to: string;
  active: boolean;
  /** Optional count/marker shown beside the label when expanded. */
  badge?: string | number;
  /**
   * Page-level tabs shown as `Tabs` on desktop and nested under this parent in
   * the mobile drawer (US1-AC3).
   */
  secondary?: NavSecondaryItem[];
}

export interface NavModel {
  destinations: NavDestination[];
  /** Help / external links rendered in the rail/drawer footer region. */
  footerLinks: { id: string; label: string; href: string; icon: IconName }[];
}

const HELP_URL = "https://intercom.help/tokeniser/en/";

export const useNavDestinations = (): NavModel => {
  const location = useLocation();
  // eslint-disable-next-line prefer-const
  let { companyId, shareholderId } = useParams<{ companyId: string; shareholderId: string }>();
  const { currentUser } = React.useContext(AuthContext);

  if (!shareholderId) {
    const currentShareholding = currentUser?.shareholdings?.find(
      (holding: { companyId: string }) => holding.companyId === companyId
    );
    shareholderId = currentShareholding?.shareholderId || "";
  }

  const {
    isFundRegistry,
    registryType,
    displayRequestBadge,
    features,
    isAgentAuthorized,
    isLoadingCompanyInfo,
    selectedSubscriptionPlan,
    userCompanyRoles,
    distributionConfig,
    redemptionConfig,
    subscriptionConfig,
  } = useCompanyContext();

  const shouldDisplayBadge = !isLoadingCompanyInfo && features["add-investors"] && displayRequestBadge;

  const [pendingApprovalCount, setPendingApprovalCount] = React.useState(0);
  React.useEffect(() => {
    if (!features.approvals || currentUser?.isInvestor || !companyId) return undefined;
    setPendingApprovalCount(0);
    let mounted = true;
    approvalService
      .getPendingApprovalCount(companyId)
      .then((res) => {
        if (mounted && res.status && res.payload) setPendingApprovalCount(res.payload.count);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [features.approvals, companyId, currentUser?.isInvestor]);

  React.useEffect(() => {
    if (!features.approvals || currentUser?.isInvestor || !companyId) return undefined;
    let mounted = true;
    const handleCountStale = () => {
      approvalService
        .getPendingApprovalCount(companyId)
        .then((res) => {
          if (mounted && res.status && res.payload) setPendingApprovalCount(res.payload.count);
        })
        .catch(() => {});
    };
    window.addEventListener(APPROVAL_COUNT_CHANGED_EVENT, handleCountStale);
    return () => {
      mounted = false;
      window.removeEventListener(APPROVAL_COUNT_CHANGED_EVENT, handleCountStale);
    };
  }, [features.approvals, companyId, currentUser?.isInvestor]);

  // Hidden while the yield settings load or if they fail; the page itself reports the error.
  const { yieldFund: showPendingAccruals } = useYieldFund(companyId, {
    enabled: isAgentAuthorized && !isLoadingCompanyInfo && userCompanyRoles.isAdmin,
  });
  const [pendingAccrualCount, setPendingAccrualCount] = React.useState(0);
  const [pendingAccrualVersion, setPendingAccrualVersion] = React.useState(0);
  React.useEffect(() => {
    const onChanged = (): void => setPendingAccrualVersion((version) => version + 1);
    window.addEventListener(PENDING_ACCRUED_UNITS_CHANGED, onChanged);
    return () => window.removeEventListener(PENDING_ACCRUED_UNITS_CHANGED, onChanged);
  }, []);

  React.useEffect(() => {
    if (!showPendingAccruals || !companyId) {
      setPendingAccrualCount(0);
      return undefined;
    }
    let mounted = true;
    companyService
      .getPendingAccrualMints(companyId)
      .then((res) => {
        if (mounted) setPendingAccrualCount(res.payload?.count ?? 0);
      })
      // A failed count only hides the dot; the page itself reports the error.
      .catch(() => {
        if (mounted) setPendingAccrualCount(0);
      });
    return () => {
      mounted = false;
    };
  }, [showPendingAccruals, companyId, pendingAccrualVersion]);

  // Active-link detection — preserves the exact rules from the legacy Sidebar so
  // existing routes keep their highlighted parent (no behavioural change).
  const checkActiveLink = React.useCallback(
    (linkName: string): boolean => {
      const pathnames = location.pathname.split("/");
      const pageLinks: string[] = navbarDataMap[linkName] ? navbarDataMap[linkName].map((item) => item.value) : [];

      const investorProfilesRoutes = [
        `/investor/company/${companyId}/investor-profiles/active`,
        `/investor/company/${companyId}/investor-profiles/requests`,
        `/investor/company/${companyId}/investor-profiles/existing`,
        `/investor/company/${companyId}/investor-profiles/add`,
        `/investor/company/${companyId}/switch-holding`,
      ];

      if (linkName === "Registers") {
        pageLinks.push("investor-holdings");
      } else if (linkName === "Investors") {
        return location.pathname.includes(`/company/${companyId}/investors/`);
      } else if (linkName === "Investor profiles") {
        return investorProfilesRoutes.includes(location.pathname);
      } else if (linkName === "Investments") {
        return (
          location.pathname.includes(`/investor/company/${companyId}/investor-view/${shareholderId}`) &&
          !location.pathname.endsWith("/redeem") &&
          !location.pathname.includes("/apply")
        );
      }
      return pathnames.some((path: string) => pageLinks.includes(path));
    },
    [location.pathname, companyId, shareholderId]
  );

  // Prefix match — mirrors react-router NavLink's default (non-exact) active
  // matching that the legacy sidebar relied on for destinations without bespoke
  // active rules (Admin view, Activity, Documents, Company Information, …).
  const isRouteActive = React.useCallback(
    (base: string): boolean => location.pathname.startsWith(base),
    [location.pathname]
  );

  return React.useMemo<NavModel>(() => {
    const destinations: NavDestination[] = [];

    if (currentUser?.isInvestor) {
      destinations.push({
        id: "investor-profiles",
        icon: "user",
        label: "Investor profiles",
        to: `/investor/company/${companyId}/investor-profiles/active`,
        active: checkActiveLink("Investor profiles"),
      });

      if (!userCompanyRoles.isExternalInvestor && shareholderId) {
        destinations.push({
          id: "investments",
          icon: "briefcase",
          label: "Investments",
          to: `/investor/company/${companyId}/investor-view/${shareholderId}/switch-holding`,
          active: checkActiveLink("Investments"),
        });
      }
      if (
        isCapabilityAvailable(FeatureIds.REDEMPTIONS, registryType) &&
        redemptionConfig?.allowRedemptions === true &&
        shareholderId
      ) {
        destinations.push({
          id: "redeem",
          icon: "arrowDownRight",
          label: "Redeem",
          to: `/investor/company/${companyId}/investor-view/${shareholderId}/redeem`,
          active: isRouteActive(`/investor/company/${companyId}/investor-view/${shareholderId}/redeem`),
        });
      }

      if (
        isSubscriptionsOffered(features, registryType) &&
        subscriptionConfig?.allowSubscriptions === true &&
        shareholderId
      ) {
        destinations.push({
          id: "apply",
          icon: "arrowUpRight",
          label: "Apply for units",
          to: `/investor/company/${companyId}/investor-view/${shareholderId}/apply`,
          active: isRouteActive(`/investor/company/${companyId}/investor-view/${shareholderId}/apply`),
        });
      }

      if (selectedSubscriptionPlan !== "plan1" && !userCompanyRoles.isExternalInvestor && shareholderId) {
        destinations.push({
          id: "announcements",
          icon: "bell",
          label: "Announcements",
          to: `/investor/company/${companyId}/investor-relations/investor/${shareholderId}/announcement`,
          active: isRouteActive(`/investor/company/${companyId}/investor-relations`),
        });
      }
    } else if (isAgentAuthorized) {
      destinations.push({
        id: "admin-view",
        icon: "dashboard",
        // APPROVER-only users see the dashboard labelled "Approver view".
        label: isApproverReadOnly(userCompanyRoles) ? "Approver view" : "Admin view",
        to: `/company/${companyId}/admin-view`,
        active: isRouteActive(`/company/${companyId}/admin-view`),
      });

      // Registers — secondary tabs surface as nested drawer items / desktop Tabs.
      const registerSecondary: NavSecondaryItem[] = (navbarDataMap.Registers ?? []).map((item) => ({
        id: item.value,
        label: item.label,
        to: `/company/${companyId}/${item.value}`,
        active: location.pathname.includes(`/${item.value}`),
      }));
      destinations.push({
        id: "registers",
        icon: "fileText",
        label: "Registers",
        to: `/company/${companyId}/shares-register`,
        active: checkActiveLink("Registers"),
        secondary: registerSecondary,
      });

      if (showPendingAccruals) {
        destinations.push({
          id: "pending-accruals",
          icon: "clock",
          label: "Pending Accruals",
          to: `/company/${companyId}/pending-accrued-units`,
          active: isRouteActive(`/company/${companyId}/pending-accrued-units`),
          badge: pendingAccrualCount > 0 ? "•" : undefined,
        });
      }

      if (features["fundraise-features"]) {
        destinations.push({
          id: "fundraising",
          icon: "coins",
          label: "Fundraising",
          to: `/company/${companyId}/fundraises`,
          active: checkActiveLink("Fundraising"),
        });
      }

      if (features["admin-investors-list-page"]) {
        destinations.push({
          id: "investors",
          icon: "users",
          label: "Investors",
          to: `/company/${companyId}/investors/all`,
          active: checkActiveLink("Investors"),
          badge: shouldDisplayBadge ? "•" : undefined,
        });
      }

      if (selectedSubscriptionPlan !== "plan1") {
        destinations.push({
          id: "investor-relations",
          icon: "messageSquare",
          label: "Investor Relations",
          to: `/company/${companyId}/investor-relations/announcements`,
          active: checkActiveLink("Investor Relations"),
        });
      }

      if (isDistributionsOffered(features, registryType, distributionConfig)) {
        destinations.push({
          id: "distributions",
          icon: "wallet",
          label: "Distributions",
          to: `/company/${companyId}/distributions`,
          active: isRouteActive(`/company/${companyId}/distributions`),
        });
      }

      if (isCapabilityAvailable(FeatureIds.REDEMPTIONS, registryType) && redemptionConfig?.allowRedemptions === true) {
        destinations.push({
          id: "redemptions",
          icon: "arrowDownRight",
          label: "Redemptions",
          to: `/company/${companyId}/redemptions`,
          active: isRouteActive(`/company/${companyId}/redemptions`),
        });
      }

      // Not gated on `allowSubscriptions`: pausing intake must not hide the applications already lodged.
      if (isSubscriptionsOffered(features, registryType)) {
        destinations.push({
          id: "subscriptions",
          icon: "arrowUpRight",
          label: "Subscriptions",
          to: `/company/${companyId}/subscriptions`,
          active: isRouteActive(`/company/${companyId}/subscriptions`),
        });
      }

      destinations.push({
        id: "documents",
        icon: "stickyNote",
        label: "Documents",
        to: `/company/${companyId}/documents`,
        active: isRouteActive(`/company/${companyId}/documents`),
      });
      destinations.push({
        id: "activity",
        icon: "history",
        label: "Activity",
        to: `/company/${companyId}/activity`,
        active: isRouteActive(`/company/${companyId}/activity`),
      });

      if (features.approvals) {
        destinations.push({
          id: "approvals",
          icon: "checkCircle",
          label: "Approvals",
          to: `/company/${companyId}/approvals`,
          active: isRouteActive(`/company/${companyId}/approvals`),
          badge: pendingApprovalCount > 0 ? "•" : undefined,
        });
      }

      destinations.push({
        id: "company-information",
        icon: "briefcase",
        label: isFundRegistry ? "Unit Registry" : "Company Information",
        to: `/company/${companyId}/company-information`,
        active: isRouteActive(`/company/${companyId}/company-information`),
      });
      destinations.push({
        id: "settings",
        icon: "settings",
        label: "Settings",
        to: `/company/${companyId}/settings`,
        active: isRouteActive(`/company/${companyId}/settings`),
      });
    }

    return {
      destinations,
      footerLinks: [{ id: "help", label: "Help", href: HELP_URL, icon: "helpCircle" }],
    };
  }, [
    currentUser?.isInvestor,
    companyId,
    shareholderId,
    isAgentAuthorized,
    features,
    isFundRegistry,
    registryType,
    distributionConfig,
    redemptionConfig,
    subscriptionConfig,
    selectedSubscriptionPlan,
    userCompanyRoles.isExternalInvestor,
    userCompanyRoles.isApprover,
    userCompanyRoles.isAdmin,
    shouldDisplayBadge,
    pendingApprovalCount,
    showPendingAccruals,
    pendingAccrualCount,
    checkActiveLink,
    isRouteActive,
    location.pathname,
  ]);
};
